import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import React from "react";
import { useForm } from "react-hook-form";

import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/context";
import { useI18n } from "@/context/i18n";

import { whatsappMetaConfigApi } from "./whatsapp-meta-config.api";
import type { IUseWhatsappMetaConfig } from "./whatsapp-meta-config.hooks.interface";
import { whatsappMetaConfigQueryKeys } from "./whatsapp-meta-config.query-keys";
import {
  createWhatsappMetaFormSchema,
  editWhatsappMetaFormSchema,
  type TWhatsappMetaEditFormData,
} from "./whatsapp-meta-config.schema";
import type { TWhatsappMetaConfig } from "./whatsapp-meta-config.types";

function readOrganizationIdFromCookie(): string | undefined {
  try {
    const raw = Cookies.get("authUser");
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as { organization_root_id?: string };
    const id = parsed.organization_root_id?.trim();
    return id || undefined;
  } catch {
    return undefined;
  }
}

export const useWhatsappMetaConfig = (
  externalOrganizationId?: string,
): IUseWhatsappMetaConfig => {
  const { t } = useI18n();
  const { auth } = useAuth();
  const qc = useQueryClient();

  /** Mesma fonte que customFetch: cookie tem org antes do contexto React em alguns renders. */
  const organizationId = React.useMemo(() => {
    if (externalOrganizationId?.trim()) {
      return externalOrganizationId.trim();
    }
    const fromAuth = auth.organization_root_id?.trim();
    if (fromAuth) {
      return fromAuth;
    }
    return readOrganizationIdFromCookie();
  }, [externalOrganizationId, auth.organization_root_id, auth.token]);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = React.useState<string | null>(
    null
  );

  const requiredMsg = t("validationMessages.requiredMessage");

  const formSchema = React.useMemo(
    () =>
      editingId
        ? editWhatsappMetaFormSchema(requiredMsg)
        : createWhatsappMetaFormSchema(requiredMsg),
    [editingId, requiredMsg]
  );

  const form = useForm<TWhatsappMetaEditFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      label: "",
      waba_id: "",
      access_token: "",
    },
  });

  const {
    data: configs = [],
    isLoading,
    isError: isConfigsError,
    error: configsQueryError,
  } = useQuery({
    queryKey: [...whatsappMetaConfigQueryKeys.configs, organizationId ?? ""],
    queryFn: () => whatsappMetaConfigApi.list(),
    enabled: Boolean(organizationId),
  });

  const configsErrorMessage = React.useMemo(() => {
    if (!configsQueryError) {
      return null;
    }
    return configsQueryError instanceof Error
      ? configsQueryError.message
      : String(configsQueryError);
  }, [configsQueryError]);

  const createMutation = useMutation({
    mutationFn: (body: Parameters<typeof whatsappMetaConfigApi.create>[0]) =>
      whatsappMetaConfigApi.create(body, organizationId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: whatsappMetaConfigQueryKeys.configs,
        refetchType: "all",
      });
      toast({ title: t("campaigns.whatsappMeta.success.created") });
      closeDialog();
    },
    onError: (err: Error) => {
      toast({
        title: t("generic.error"),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof whatsappMetaConfigApi.update>[1];
    }) => whatsappMetaConfigApi.update(id, body, organizationId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: whatsappMetaConfigQueryKeys.configs,
        refetchType: "all",
      });
      void qc.invalidateQueries({
        queryKey: whatsappMetaConfigQueryKeys.phoneNumbers(variables.id),
      });
      void qc.invalidateQueries({
        queryKey: whatsappMetaConfigQueryKeys.templates(variables.id),
      });
      toast({ title: t("campaigns.whatsappMeta.success.updated") });
      closeDialog();
    },
    onError: (err: Error) => {
      toast({
        title: t("generic.error"),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => whatsappMetaConfigApi.remove(id, organizationId),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({
        queryKey: whatsappMetaConfigQueryKeys.configs,
        refetchType: "all",
      });
      toast({ title: t("campaigns.whatsappMeta.success.deleted") });
      setDeleteTargetId(null);
    },
    onError: (err: Error) => {
      toast({
        title: t("generic.error"),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const openCreate = React.useCallback(() => {
    setEditingId(null);
    form.reset({
      label: "",
      waba_id: "",
      access_token: "",
    });
    setDialogOpen(true);
  }, [form]);

  const openEdit = React.useCallback(
    (row: TWhatsappMetaConfig) => {
      setEditingId(row.id);
      form.reset({
        label: row.label ?? "",
        waba_id: row.waba_id,
        access_token: "",
      });
      setDialogOpen(true);
    },
    [form]
  );

  const closeDialog = React.useCallback(() => {
    setDialogOpen(false);
    setEditingId(null);
    form.reset({
      label: "",
      waba_id: "",
      access_token: "",
    });
  }, [form]);

  const onSubmit = React.useCallback(
    (data: TWhatsappMetaEditFormData) => {
      if (editingId) {
        const body: Parameters<typeof whatsappMetaConfigApi.update>[1] = {
          label: data.label,
          waba_id: data.waba_id,
        };
        if (data.access_token?.trim()) {
          body.access_token = data.access_token.trim();
        }
        updateMutation.mutate({ id: editingId, body });
        return;
      }
      createMutation.mutate({
        label: data.label,
        waba_id: data.waba_id,
        access_token: data.access_token?.trim() ?? "",
      });
    },
    [createMutation, editingId, updateMutation]
  );

  const onDelete = React.useCallback(
    (id: string) => {
      deleteMutation.mutate(id);
    },
    [deleteMutation]
  );

  return {
    configs,
    isLoading,
    isConfigsError,
    configsErrorMessage,
    dialogOpen,
    editingId,
    form,
    openCreate,
    openEdit,
    closeDialog,
    onSubmit,
    isSaving: createMutation.isPending || updateMutation.isPending,
    deleteTargetId,
    setDeleteTargetId,
    onDelete,
    isDeleting: deleteMutation.isPending,
  };
};
